import React, {HTMLInputTypeAttribute, useContext} from 'react';
import {Box, TextField, TextFieldPropsColorOverrides} from '@mui/material';
import {OverridableStringUnion} from '@mui/types';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {ThemeMode} from '../../enums/theme-mode';
import {ErrorWithCharacterCount} from '../error-with-character-count/error-with-character-count';
import {Tooltip} from '../tooltip/tooltip';

/**
 * Text input field.
 */
export const TextInput = (props: {
  label: string;
  value: string;
  handleChange: (value: string) => void;
  required?: boolean;
  error?: string;
  characterCountError?: {
    error: string;
    maxCharacters: number;
    characterCount: number;
  };
  id?: string;
  className?: string;
  handleBlur?: () => void;
  multiline?: boolean;
  type?: HTMLInputTypeAttribute;
  autoComplete?: string;
  disabled?: boolean;
  tooltipTitle?: string;
  notAllowedEditing?: boolean;
  min?: string;
  rows?: number;
  startAdornment?: React.ReactNode;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    TextFieldPropsColorOverrides
  >;
}) => {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  const textInput: React.ReactElement = (
    <Box className={!props.tooltipTitle ? props.className : undefined} id={props.id}>
      <TextField
        label={props.label}
        value={props.value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          props.handleChange(event.target.value)
        }
        onBlur={props.handleBlur}
        error={props.error || props.characterCountError ? true : false}
        helperText={props.error && !props.characterCountError ? props.error : undefined}
        disabled={props.disabled === true || props.notAllowedEditing === true ? true : false}
        required={props.required === true ? true : false}
        multiline={props.multiline === true ? true : false}
        rows={props.rows || 2}
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{min: props.min}}
        InputProps={{
          startAdornment: props.startAdornment || undefined,
        }}
        variant="outlined"
        color={props.color ?? 'neutral'}
        type={props.type}
        autoComplete={props.autoComplete}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-input': {
            cursor: props.notAllowedEditing === true ? 'not-allowed !important' : undefined,
          },
          '& .MuiInputLabel-root': {
            color:
              props.notAllowedEditing === true && props.disabled !== true
                ? appContext.state.currentThemeMode === ThemeMode.Light
                  ? 'rgba(0, 0, 0, 0.6)'
                  : 'rgba(255, 255, 255, 0.7)'
                : undefined,
          },
        }}
      />
      {!props.error && props.characterCountError && (
        <ErrorWithCharacterCount
          error={props.characterCountError.error}
          characterCount={props.characterCountError.characterCount}
          maxCharacters={props.characterCountError.maxCharacters}
        />
      )}
    </Box>
  );

  return (
    <>
      {props.tooltipTitle ? (
        <Tooltip className={props.className} title={props.tooltipTitle}>
          {textInput}
        </Tooltip>
      ) : (
        textInput
      )}
    </>
  );
};
